import { useQuery } from '@apollo/client';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { gqlTypes } from '../../../../types';
import {
  activeBillingSubscriptionsDetailsQuery,
  allSubscriptionsForCurrentUserQuery,
} from '../../../../graphql-operations';
import { getDomainConfig } from '../../../../lib/domainConfig';
import { getLoadingOrErrorElement } from '../../../../lib/util';
import { UserHouseholdRole } from '../../../../types/gql-op-types';
import { Image } from '../../../../components/Image';
import { DateTime } from 'luxon';
import { Accordion, Alert, Anchor, Badge, Group, Button as MantineButton, Stack, Text } from '@mantine/core';
import CreditCardIcon from '../../../../assets/images/icons/icon_credit_card_outline.svg';
import AddIcon from '../../../../assets/images/icons/add.svg';
import HelpIcon from '../../../../assets/images/icons/icon_help.svg';
import AppBar from '../../../../components/AppBar';
import { useLocation } from 'react-router-dom';
import { getShortSubscriptionDescription } from '../../util/helpers';
import { webSubscriptionManage as events } from '../../../../lib/analytics/events';
import { BaseManageSubscriptionState } from '../util/types';
import { SubscriptionCard } from './SubscriptionCard';
import { SimplePetDisplay } from './SimplePetDisplay';
import UnassignedSubscriptionCard from './UnassignedSubscriptionCard';
import styles from './Overview.module.scss';

export default function Overview() {
  const { t } = useTranslation();
  const location = useLocation<BaseManageSubscriptionState>();

  events.overview.pageLoad();

  const {
    loading,
    error,
    data,
    refetch: refetchAllSubscriptions,
  } = useQuery<gqlTypes.ECOMMERCE_allSubscriptionsForCurrentUser>(allSubscriptionsForCurrentUserQuery);

  const {
    loading: billingQueryLoading,
    error: billingQueryError,
    data: billingQueryData,
  } = useQuery<gqlTypes.ECOMMERCE_activeBillingSubscriptionsDetails>(activeBillingSubscriptionsDetailsQuery);

  const loadingOrErrorElement =
    getLoadingOrErrorElement(loading, error) ?? getLoadingOrErrorElement(billingQueryLoading, billingQueryError);
  if (loadingOrErrorElement) {
    return loadingOrErrorElement;
  }

  const householdsWhereOwner = data?.currentUser.userHouseholds.filter(
    (uh) => uh.householdRole === UserHouseholdRole.OWNER,
  );
  const allPets = householdsWhereOwner?.flatMap((h) => h.household.pets);
  const petsWithDevices = allPets?.filter((p) => p.device !== null && p.device !== undefined);
  const petsWithoutDevices = allPets?.filter((p) => p.device === null || p.device === undefined);
  const hasPetsWithoutDevices = !!petsWithoutDevices && petsWithoutDevices.length > 0;

  const subscriptionsWithDevices = new Set<string>();
  petsWithDevices?.forEach((p) => {
    if (p.device?.subscriptionId) {
      subscriptionsWithDevices.add(p.device?.subscriptionId);
    }
  });
  const subscriptionsWithoutDevices = billingQueryData?.currentUser.billingAccount?.subscriptions?.filter(
    (s) => !subscriptionsWithDevices.has(s.id),
  );
  const hasSubscriptionsWithoutDevices = !!subscriptionsWithoutDevices && subscriptionsWithoutDevices.length > 0;

  const subscriptionCards = petsWithDevices?.map((p) => (
    <SubscriptionCard
      petName={p.name}
      petSpecies={p.species ?? undefined}
      avatarUrl={p.photos.first?.image.fullSize ?? undefined}
      petId={p.id}
      hasSubscription={!!p.device?.subscription}
      subscriptionId={p.device?.subscriptionId}
      moduleId={p.device?.moduleId}
      modelName={p.device?.currentCollarModelLongName ?? undefined}
      renewalDate={DateTime.fromISO(p.device?.subscription?.currentTermEndsAt)}
      expirationDate={DateTime.fromISO(p.device?.subscription?.expiresAt)}
      key={p.name}
      priceInCents={p.device?.subscription?.subscriptionOption?.priceInCents}
      renewalPriceInCents={p.device?.subscription?.nextRenewalPreview?.totalInCents}
      renewalMonths={p.device?.subscription?.subscriptionOption?.renewalMonths}
      userEmail={data?.currentUser.email}
      billingEmail={p.device?.subscription?.billingEmail}
      isCanceled={p.device?.subscription?.isCanceled ?? false}
      hasFreeTrialPlan={p.device?.subscription?.hasFreeTrialPlan ?? false}
      isInTrialPeriod={p.device?.subscription?.isInTrialPeriod ?? false}
      sku={p.device?.subscription?.subscriptionOption?.sku}
      isPaused={p.device?.subscription?.isPaused ?? false}
      pausedAt={DateTime.fromISO(p.device?.subscription?.pausedAt)}
      pendingCxResponse={p.device?.cxPendingAction !== null}
      location={location}
      pendingChange={
        p.device?.subscription?.pendingChanges?.subscriptionOption
          ? {
              priceInCents: p.device.subscription.pendingChanges.subscriptionOption?.priceInCents,
              term: getShortSubscriptionDescription(
                t,
                p.device.subscription.pendingChanges.subscriptionOption.renewalMonths,
              ),
            }
          : undefined
      }
      lastPaymentFailed={p.device?.subscription?.lastPaymentFailed ?? false}
      refetchAllSubscriptions={refetchAllSubscriptions}
    />
  ));

  const petsWithoutDevicesDisplays = petsWithoutDevices?.map((p) => (
    <SimplePetDisplay petName={p.name} petSpecies={p.species} avatarUrl={p.photos.first?.image.fullSize ?? undefined} />
  ));

  const subscriptionsWithoutDevicesDisplays = subscriptionsWithoutDevices?.map((s) => (
    <UnassignedSubscriptionCard
      renewalMonths={s.subscriptionOption?.renewalMonths}
      renewalDate={DateTime.fromISO(s.currentTermEndsAt)}
      isCanceled={s.isCanceled ?? false}
      expirationDate={DateTime.fromISO(s.expiresAt)}
      subscriptionId={s.id}
    />
  ));

  return (
    <>
      <AppBar />
      <Stack spacing={16} w={'100%'} maw={668} px={24}>
        {subscriptionCards}
        {data?.currentUser.editBillingInfoUrl && (
          <MantineButton
            leftIcon={<Image image={{ type: 'image', url: CreditCardIcon }} />}
            component="a"
            href={data.currentUser.editBillingInfoUrl}
            target="_blank"
            variant="outline"
            color="dark"
            radius="lg"
          >
            {t('edit_payment')}
          </MantineButton>
        )}
        {hasPetsWithoutDevices || hasSubscriptionsWithoutDevices ? (
          <Accordion
            chevron={<Image image={{ type: 'image', url: AddIcon }} />}
            styles={{
              chevron: {
                '&[data-rotate]': {
                  transform: 'rotate(45deg)',
                },
              },
            }}
          >
            {hasPetsWithoutDevices && (
              <Accordion.Item value="pets">
                <Accordion.Control>
                  <Group>
                    <Text fz={18} fw={600} color="type-light-primary">
                      {t('pets_without_devices')}
                    </Text>
                    <Badge color="fi-yellow" variant="filled">
                      <Text color="dark">{petsWithoutDevices.length}</Text>
                    </Badge>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack>{petsWithoutDevicesDisplays}</Stack>
                </Accordion.Panel>
              </Accordion.Item>
            )}
            {hasSubscriptionsWithoutDevices && (
              <Accordion.Item value="subscriptions_without_devices">
                <Accordion.Control>
                  <Group>
                    <Text fz={18} fw={600} color="type-light-primary">
                      {t('unassigned_memberships')}
                    </Text>
                    <Badge color="fi-yellow" variant="filled">
                      <Text color="dark">{subscriptionsWithoutDevices.length}</Text>
                    </Badge>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack>
                    <Alert
                      icon={<Image image={{ type: 'image', url: HelpIcon }} height={20} width={20} />}
                      title={t('unassigned_subscriptions_title')}
                      radius={24}
                      pr={30}
                    >
                      <Text>{t('unassigned_subscriptions_contents')}</Text>
                      <Text>{t('unassigned_subscriptions_reasons')}</Text>
                      <ul className={styles.reasonsList}>
                        <li>
                          <Text>{t('unassigned_subscriptions_reasons_unlinked')}</Text>
                        </li>
                        <li>
                          <Text>
                            {t('unassigned_subscriptions_reasons_pack_membership')}{' '}
                            <Anchor
                              href={`https://${
                                getDomainConfig().supportDomain
                              }/hc/en-us/articles/360023796753-Add-or-Remove-a-Full-Access-Member`}
                              target="_blank"
                            >
                              {t('unassigned_subscriptions_reasons_pack_membership_learn_more')}
                            </Anchor>
                          </Text>
                        </li>
                      </ul>
                    </Alert>
                    {subscriptionsWithoutDevicesDisplays}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            )}
          </Accordion>
        ) : (
          <></>
        )}
      </Stack>
    </>
  );
}
